import { useEffect, useState } from "react";
import get from "lodash/get";
import {
  Descriptions,
  Spin,
  Card,
  Tag,
  Typography,
  List,
  Button,
  message,
  Modal,
  Input,
  Checkbox,
} from "antd";
import {
  AppstoreAddOutlined,
  CheckCircleOutlined,
  IdcardOutlined,
  KeyOutlined,
  StopOutlined,
} from "@ant-design/icons";
import { useParams } from "react-router-dom";
import { readableDateTime } from "../../utilities/helper";
import { getApi, postApi } from "../../redux/rest/restSlice";
import { unwrapResult } from "@reduxjs/toolkit";
import {
  GET_USER_DETAILS,
  UPDATE_USER_STATUS,
  UPDATE_USERS,
  LIST_ROLES,
  ASSIGN_ROLES,
  ASSIGN_CABINETS,
  SHORT_LIST_CABINETS,
} from "../../constants/actionTypes";
import { useDispatch, useSelector } from "react-redux";
import Can from "../../components/Can";
import { PERMISSIONS } from "../../constants/permissions";
import PageHeader, { useGoBack } from "../../components/PageHeader";

const { Title, Text } = Typography;

const UserDetailsScreen = () => {
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [assignRoleLoading, setAssignRoleLoading] = useState(false);
  const [assignCabinetLoading, setAssignCabinetLoading] = useState(false);

  const [passwordModalOpen, setPasswordModalOpen] = useState(false);
  const [assignRoleModalOpen, setAssignRoleModalOpen] = useState(false);
  const [assignCabinetModalOpen, setAssignCabinetModalOpen] = useState(false);

  const [newPassword, setNewPassword] = useState("");

  const [rolesList, setRolesList] = useState([]);
  const [selectedRoleIds, setSelectedRoleIds] = useState([]);

  const [cabinetList, setCabinetList] = useState([]);
  const [selectedCabinetIds, setSelectedCabinetIds] = useState([]);

  const { uuid: userUuid } = useParams();
  const dispatch = useDispatch();
  const goBack = useGoBack();
  const [usersDetails, setUsersDetails] = useState(null);


  const loggedInUserId = useSelector((state) => get(state, "auth.user.uuid"));
  const isSelfUser = userUuid === loggedInUserId;

  /* ---------------- Fetch User ---------------- */
  const getUsersDetails = () => {
    dispatch(
      getApi({
        endpoint: `${GET_USER_DETAILS}/${userUuid}`,
      })
    )
      .then(unwrapResult)
      .then((result) => {
        const data = get(result, "data");
        setUsersDetails(data);

        // Preselect roles
        setSelectedRoleIds(
          get(data, "roles_and_permissions", []).map((r) => r.id)
        );

        // Preselect cabinets
        setSelectedCabinetIds(
          get(data, "linkedCabinets", []).map((c) => c.cabinet_identifier)
        );

        setLoading(false);
      })
      .catch(() => setLoading(false));
  };

  useEffect(() => {
    if (!userUuid) return;
    getUsersDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ---------------- Toggle User Status ---------------- */
  const toggleUserStatus = () => {
    const nextStatus = !usersDetails.is_active;
    setActionLoading(true);

    dispatch(
      postApi({
        endpoint: UPDATE_USER_STATUS,
        payload: {
          uuid: userUuid,
          is_active: nextStatus,
        },
      })
    )
      .then(unwrapResult)
      .then(() => {
        message.success(`User marked ${nextStatus ? "Active" : "Inactive"}`);
        setUsersDetails((prev) => ({
          ...prev,
          is_active: nextStatus,
        }));
      })
      .finally(() => setActionLoading(false));
  };

  /* ---------------- Change Password ---------------- */
  const handleChangePassword = () => {
    if (!newPassword.trim()) {
      return message.error("New password is required");
    }

    setPasswordLoading(true);

    dispatch(
      postApi({
        endpoint: UPDATE_USERS,
        payload: {
          id: usersDetails.id,
          first_name: usersDetails.first_name,
          last_name: usersDetails.last_name,
          email_address: usersDetails.email_address,
          password: newPassword,
        },
      })
    )
      .then(unwrapResult)
      .then(() => {
        message.success("Password updated successfully");
        setPasswordModalOpen(false);
      })
      .catch((err) => {
        // message.error(err?.message || "Action failed");
      })

      .finally(() => setPasswordLoading(false));
  };

  /* ---------------- Assign Roles ---------------- */
  const openAssignRoleModal = () => {
    setAssignRoleModalOpen(true);

    dispatch(getApi({ endpoint: LIST_ROLES }))
      .then(unwrapResult)
      .then((result) => {
        setRolesList(get(result, "data", []));
      });
  };

  const handleAssignRoles = () => {
    setAssignRoleLoading(true);

    dispatch(
      postApi({
        endpoint: ASSIGN_ROLES,
        payload: {
          user_id: usersDetails.id,
          role_ids: selectedRoleIds,
        },
      })
    )
      .then(unwrapResult)
      .then(() => {
        message.success("Roles assigned successfully");
        setAssignRoleModalOpen(false);
        getUsersDetails();
      })
      .finally(() => setAssignRoleLoading(false));
  };

  /* ---------------- Assign Cabinets ---------------- */
  const openAssignCabinetModal = () => {
    setAssignCabinetModalOpen(true);

    dispatch(
      postApi({
        endpoint: SHORT_LIST_CABINETS,
      })
    )
      .then(unwrapResult)
      .then((result) => {
        setCabinetList(get(result, "data.records", []));
      });
  };

  const handleAssignCabinets = () => {
    setAssignCabinetLoading(true);

    dispatch(
      postApi({
        endpoint: ASSIGN_CABINETS,
        payload: {
          user_id: usersDetails.id,
          cabinet_ids: selectedCabinetIds,
        },
      })
    )
      .then(unwrapResult)
      .then(() => {
        message.success("Cabinets assigned successfully");
        setAssignCabinetModalOpen(false);
        getUsersDetails();
      })
      .finally(() => setAssignCabinetLoading(false));
  };

  if (loading) {
    return (
      <div className="page-loading">
        <Spin size="large" spinning />
      </div>
    );
  }

  if (!usersDetails) return null;

  const {
    first_name,
    last_name,
    email_address,
    is_active,
    created_at,
    updated_at,
    roles_and_permissions = [],
    linkedCabinets = [],
  } = usersDetails;

  return (
    <div className="page">
      <PageHeader
        onBack={goBack}
        title={[first_name, last_name].filter(Boolean).join(" ") || "--"}
      />

      {/* 🔹 User Summary */}
      <Card bordered={false} className="section-card">
        <Title level={4}>User Details</Title>

        <Descriptions column={2} bordered size="small">
          <Descriptions.Item label="Name">
            {first_name} {last_name}
          </Descriptions.Item>
          <Descriptions.Item label="Status">
            <Tag color={is_active ? "green" : "red"}>
              {is_active ? "Active" : "Inactive"}
            </Tag>
          </Descriptions.Item>
          <Descriptions.Item label="Created At">
            {readableDateTime(created_at, "dd/mm/yyyy hh:mm")}
          </Descriptions.Item>
          <Descriptions.Item label="Updated At">
            {readableDateTime(updated_at, "dd/mm/yyyy hh:mm")}
          </Descriptions.Item>
          <Descriptions.Item label="Email">{email_address}</Descriptions.Item>
        </Descriptions>
      </Card>

      {/* 🔹 Action Buttons */}
      <div className="action-bar">
        <Can permissions={[PERMISSIONS.UPDATE_USERS]}>
          <Button
            danger={is_active}
            loading={actionLoading}
            icon={is_active ? <StopOutlined /> : <CheckCircleOutlined />}
            onClick={toggleUserStatus}
          >
            {is_active ? "Mark Inactive" : "Mark Active"}
          </Button>
        </Can>

        {isSelfUser && (
          <Button
            icon={<KeyOutlined />}
            onClick={() => setPasswordModalOpen(true)}
          >
            Change Password
          </Button>
        )}

        <Can permissions={[PERMISSIONS.UPDATE_USERS]}>
          <Button icon={<IdcardOutlined />} onClick={openAssignRoleModal}>
            Assign Roles
          </Button>
        </Can>

        <Can permissions={[PERMISSIONS.UPDATE_USERS]}>
          <Button
            type="primary"
            icon={<AppstoreAddOutlined />}
            onClick={openAssignCabinetModal}
          >
            Assign/Unassign Cabinets
          </Button>
        </Can>
      </div>

      {/* 🔹 Roles & Permissions */}
      <Card bordered={false} className="section-card mt-20">
        <Title level={5}>Roles & Permissions</Title>
        {roles_and_permissions.map((roleObj) => (
          <div key={roleObj.id} className="detail-block">
            <div className="detail-block__title">
              <IdcardOutlined />
              {roleObj.role || "Unnamed Role"}
            </div>
            <div className="detail-block__tags">
              {get(roleObj, "role_permission_mappings", []).map((rpm) => (
                <Tag key={rpm.id} color="blue">
                  {rpm.permission.permission}
                </Tag>
              ))}
            </div>
          </div>
        ))}
      </Card>

      {/* 🔹 Linked Cabinets */}
      <Card bordered={false} className="section-card mt-20">
        <Title level={5}>Linked Cabinets</Title>
        <List
          bordered
          dataSource={linkedCabinets}
          renderItem={(cabinet) => (
            <List.Item>
              <Text strong>{cabinet.cabinet_name}</Text>
              <br />
              <Text type="secondary">{cabinet.cabinet_identifier}</Text>
            </List.Item>
          )}
        />
      </Card>

      {/* 🔹 Change Password Modal */}
      <Modal
        title="Change Password"
        open={passwordModalOpen}
        onCancel={() => setPasswordModalOpen(false)}
        onOk={handleChangePassword}
        confirmLoading={passwordLoading}
      >
        <Input.Password
          placeholder="Enter new password"
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
        />
      </Modal>

      {/* 🔹 Assign Roles Modal */}
      <Modal
        title="Assign Roles"
        open={assignRoleModalOpen}
        onCancel={() => setAssignRoleModalOpen(false)}
        onOk={handleAssignRoles}
        confirmLoading={assignRoleLoading}
      >
        <Checkbox.Group value={selectedRoleIds} onChange={setSelectedRoleIds}>
          <List
            dataSource={rolesList}
            renderItem={(role) => (
              <List.Item>
                <Checkbox value={role.id}>
                  {role.role || "Unnamed Role"}
                </Checkbox>
              </List.Item>
            )}
          />
        </Checkbox.Group>
      </Modal>

      {/* 🔹 Assign Cabinets Modal */}
      <Modal
        title="Assign/Unassign Cabinets"
        open={assignCabinetModalOpen}
        onCancel={() => setAssignCabinetModalOpen(false)}
        onOk={handleAssignCabinets}
        confirmLoading={assignCabinetLoading}
        width={700}
      >
        {/* Scrollable content */}
        <div
          style={{
            maxHeight: "400px",
            overflowY: "auto",
            paddingRight: 8,
          }}
        >
          <Checkbox.Group
            value={selectedCabinetIds}
            onChange={setSelectedCabinetIds}
          >
            <List
              dataSource={cabinetList}
              renderItem={(cabinet) => (
                <List.Item>
                  <Checkbox value={cabinet.cabinet_sn}>
                    <Text strong>{cabinet.name}</Text>
                    <br />
                    <Text type="secondary">{cabinet.cabinet_sn}</Text>
                  </Checkbox>
                </List.Item>
              )}
            />
          </Checkbox.Group>
        </div>
      </Modal>
    </div>
  );
};

export default UserDetailsScreen;
